// src/ui/components/BlockItem.tsx
import React, { useMemo, useEffect /* useRef */ } from 'react';
import parse from 'html-react-parser';
import Divider from '../../tools/Divider';
import { Block, getBlockSpecs, SpecItem } from '../../../../core/models/Block';
import FullscreenDialog from '../../dialogs/FullscreenDialog';
import { removeEmptyTags, sanitizeHtml } from '../../../../core/helpers/htmlSanitizer';
import { createParserOptions } from '../../../../core/helpers/createParserOptions';
import { extractFirstMedia } from '../../../../core/helpers/extractFirstMedia';
import { isAutoFitDebugActive, addDebugLog } from '../../debug/DebugUtils';
import { validateHtmlStructure } from '../../../../core/helpers/htmlValidator';
//import { capitalizeFirst } from '../../../../core/utils/text';

export interface BlockItemProps {
  block: Block;
  showSpecs?: boolean;
  activeBlockId?: number;
  showMedia?: boolean;
  showExpanded?: boolean;
  useRowLayout?: boolean;
  showFullScreen?: boolean;
}

const BlockItem: React.FC<BlockItemProps> = ({
  block,
  showSpecs = true,
  activeBlockId = 0,
  showMedia = false,
  showExpanded,
  useRowLayout = false,
  showFullScreen = false,
}) => {
  const specs = getBlockSpecs(block);
  const html = block.work;
  const listMatch = html.match(/<ul[\s\S]*?<\/ul>/);
  const listHtml = listMatch ? listMatch[0] : null;
  const cleanHtml = listHtml
    ? listHtml
      .replace(/ style="[^"]*"/g, '')
      .replace(/ class="[^"]*"/g, '')
      .replace(/ id="[^"]*"/g, '')
      .replace(/ (width|height)="[^"]*"/g, '')
    : null;

  // 1) sanitiza el HTML original (incluye media si showMedia=true)
  const sanitizedAllowMedia = useMemo(() => sanitizeHtml(html || '', true), [html]);
  // 2) Extraemos el primer media (video > iframe > img) desde el HTML ya sanitizado (con media permitido)
  const { mediaHtml, contentHtml } = useMemo(
    () => extractFirstMedia(sanitizedAllowMedia, { onlyVideo: false, fallbackToIframe: true }),
    [sanitizedAllowMedia],
  );

  // 3) Vuelve a sanear el contentHtml pero sin permitir media (esto es lo que mostrarás como texto)
  const sanitizedContent = useMemo(() => sanitizeHtml(contentHtml || '', false), [contentHtml]);

  // 4) Limpia tags vacíos del contenido final que se mostrará en la card
  const cleanedWork = useMemo(() => removeEmptyTags(sanitizedContent), [sanitizedContent]);

  // parser options (card y dialog)
  const parserOptions = useMemo(
    () =>
      createParserOptions({
        title: block.title,
        showMedia: !!showMedia,
        showExpanded,
        showFullScreen,
        useRowLayout: !!useRowLayout,
        cleanHtml,
      }),
    [block.title, showMedia, showExpanded, showFullScreen, useRowLayout, cleanHtml],
  );
  const parserOptionsDialog = useMemo(
    () =>
      createParserOptions({
        title: block.title,
        showMedia: !!showMedia,
        showExpanded,
        showFullScreen,
        useRowLayout: !!useRowLayout,
        cleanHtml,
      }),
    [block.title, showMedia, showExpanded, showFullScreen, useRowLayout, cleanHtml],
  );

  /*const dialogRef = useRef<HTMLDialogElement | null>(null);*/ // usado por FullscreenDialogBlock internamente (si lo necesitas)

  // --- Extraer URL si mediaHtml es <img ...>
  const bgImageUrl = useMemo(() => {
    if (!mediaHtml) return '';
    const srcMatch =
      mediaHtml.match(/src\s*=\s*["']([^"']+)["']/i) || mediaHtml.match(/src\s*=\s*([^>\s]+)/i);
    if (srcMatch) return srcMatch[1];
    const dataSrcMatch =
      mediaHtml.match(/data-src\s*=\s*["']([^"']+)["']/i) ||
      mediaHtml.match(/data-src\s*=\s*([^>\s]+)/i);
    if (dataSrcMatch) return dataSrcMatch[1];
    return '';
  }, [mediaHtml]);

  // --- Ajustes visuales para el fondo (transparencia / blur)
  const bgOpacity = 0.08; // 0 .. 1
  const bgBlur = 2; // px -> ajusta para suavizar la imagen

  // --- containerBase: si hay imagen la dejamos como container con overflow-hidden para que la capa absoluta la muestre
  // Mostramos la imagen siempre cuando exista bgImageUrl (ignora showMedia)
  const hasBg = !!bgImageUrl;
  const containerBase = `relative rounded-[24px] pl-3 pr-4 pt-1.5 pb-1.5 transition hover:shadow-md ${activeBlockId === block.id ? ' border-2 border-white/50' : ''
    } ${hasBg ? 'overflow-hidden shadow' : 'bg-gray-200/25 shadow'}`;


  // Función para obtener el ícono correcto según el tipo de spec
  const getSpecIcon = (iconName: string): string => {
    const iconMap: Record<string, string> = {
      clock_bolt: '/assets/icons/clock-hour-9.png',    // Timecap
      medal: '/assets/icons/pennant-2.png',            // Objetivo
      clock_heart: '/assets/icons/clock-hour-9.png',   // Rest
      users: '/assets/icons/cards.png',                // Team
      route_square: '/assets/icons/run.png',          // Marca
    };
    return iconMap[iconName] || '/assets/icons/clock-hour-9.png';
  };

  // Logging cuando el bloque está expandido
  useEffect(() => {
    if (showExpanded && isAutoFitDebugActive()) {
      const htmlValidation = validateHtmlStructure(html || '');

      addDebugLog('BlockItem', 'Bloque expandido detectado', {
        blockId: block.id,
        blockTitle: block.title,
        htmlLength: html?.length || 0,
        cleanedWorkLength: cleanedWork?.length || 0,
        hasMedia: !!mediaHtml,
        mediaType: mediaHtml ? (mediaHtml.includes('<img') ? 'image' : mediaHtml.includes('<iframe') ? 'iframe' : mediaHtml.includes('<video') ? 'video' : 'unknown') : 'none',
        htmlValidation: {
          isValid: htmlValidation.isValid,
          warningsCount: htmlValidation.warnings.length,
          errorsCount: htmlValidation.errors.length,
          warnings: htmlValidation.warnings,
          errors: htmlValidation.errors,
          unclosedTags: htmlValidation.details.unclosedTags,
          malformedAttributes: htmlValidation.details.malformedAttributes,
        },
      });

      // Si hay problemas con el HTML, loggear advertencia
      if (!htmlValidation.isValid || htmlValidation.warnings.length > 0) {
        console.warn('[BlockItem DEBUG] HTML con problemas detectado:', {
          blockId: block.id,
          blockTitle: block.title,
          validation: htmlValidation,
        });
      }
    }
  }, [showExpanded, block.id, block.title, html, cleanedWork, mediaHtml]);

  return (
    <div key={block.id} className={containerBase}>
      {/* capa de fondo absoluta: aplicamos blur/opacidad sin afectar al contenido */}
      {hasBg ? (
        <>
          {/* 1) base gris (debajo de todo) */}
          <div
            className="absolute inset-0 rounded-[24px] pointer-events-none bg-gray-100/25"
            aria-hidden="true"
            style={{ boxShadow: '0 12px 30px rgba(2,6,23,0.45), 0 4px 8px rgba(2,6,23,0.2)' }}
          />

          {/* 3) imagen en z-10, pero recortada por mask-image: solo la parte donde la máscara es opaca se muestra.
        La máscara debe ser el INVERSO del degradado visual (opaco = mostrar, transparente = ocultar).
        Aquí usamos -webkit-mask-image para compatibilidad Safari. */}
          <div
            className="absolute inset-0 rounded-[24px] bg-center bg-cover bg-no-repeat pointer-events-none"
            aria-hidden="true"
            style={{
              zIndex: 10,
              backgroundImage: `url("${bgImageUrl}")`,
              opacity: bgOpacity,
              filter: `blur(${bgBlur}px) saturate(0.95)`,
              backgroundPosition: 'center center',
              backgroundSize: 'cover',
              /* mask: donde sea negro/opaquetras la imagen se verá;
           usamos un degradado que vaya de opaco -> transparente para "recortar" la foto */
              maskImage: 'linear-gradient(135deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
              WebkitMaskImage: 'linear-gradient(135deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
              maskMode: 'alpha',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
            }}
          />

          {/* 4) overlay ligero (opcional) por encima de la imagen para contraste del texto */}
          <div
            className="absolute inset-0 rounded-[24px] pointer-events-none"
            aria-hidden="true"
            style={{ zIndex: 15, background: 'rgba(0,0,0,0.06)' }}
          />
        </>
      ) : null}

      {/* overlay ligero para contraste */}
      {hasBg ? (
        <div
          className="absolute inset-0 rounded-[24px] bg-black/10 pointer-events-none"
          style={{ zIndex: 1 }}
        />
      ) : null}

      {/* contenido en primer plano */}
      <div className="relative z-10">
        {/* Cabecera: título y specs alineados horizontalmente */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-0.5">
          {/* Título - Alineado a la izquierda */}
          <div className="min-w-0 flex-shrink pr-2">
            <h3 className="ml-1 tv-text-2xl font-bold text-white text-left m-0 p-0 break-normal">
              {block.title}
            </h3>
          </div>

          {/* Especificaciones - Alineadas a la derecha, en la misma fila si cabe */}
          {showSpecs && specs.length > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-1.5 ml-auto">
              {specs.map((row: SpecItem[], i: number) => (
                <React.Fragment key={i}>
                  {row.map((spec: SpecItem, specIdx: number) => (
                    <div
                      key={`${spec.icon}-${i}-${spec.value}-${specIdx}`}
                      className="flex items-center gap-1 text-white bg-white/10 backdrop-blur-md px-2 py-1 rounded-md border border-white/20 shadow-sm flex-shrink-0"
                    >
                      <img
                        src={getSpecIcon(spec.icon)}
                        className="w-3.5 h-3.5 opacity-90 flex-shrink-0"
                        alt={spec.title || spec.value}
                      />
                      {spec.title && spec.title.trim() && (
                        <span className="tv-text-xs text-white/70 font-medium whitespace-nowrap">
                          {spec.title}
                        </span>
                      )}
                      <span className="tv-text-xs font-bold whitespace-nowrap">
                        {spec.value}
                      </span>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>



        {block.date && (
          <p className="ml-1 text-gray-300 tv-text-2xl mb-1">{block.date.toLocaleDateString()}</p>
        )}

        <Divider className="h-[1px] w-full opacity-30 mb-1 mt-0" />

        <div className="ml-1 prose max-w-none text-white">{parse(cleanedWork, parserOptions)}</div>

      </div>

      <FullscreenDialog
        title={block.title}
        mediaHtml={mediaHtml}
        sanitizedWork={cleanedWork}
        parserOptions={parserOptionsDialog}
        showExpanded={showExpanded}
        showFullScreen={showFullScreen}
        blockSpecs={specs}
      />
    </div>
  );
};

export default BlockItem;
